/**
 * OpenAI モデル料金表
 * modelPricing（標準料金）、priorityTierPricing（優先ティア料金）、PRICING_VERSION
 */

export const PRICING_VERSION = '2026-10-27-v29'; // Recalculate GPT-6.1 Terra estimates (#171)

export interface ModelPricingEntry {
  input: number;
  cachedInput?: number;
  cacheWrite?: number;
  output: number;
}

export const modelPricing: Record<string, ModelPricingEntry> = {
  // https://developers.openai.com/api/docs/models/gpt-6-astra
  // Long-context surcharges (>272K prompt tokens) are not inferable from usage buckets.
  'gpt-6-astra': { input: 10.00 / 1_000_000, cachedInput: 1.00 / 1_000_000, cacheWrite: 12.50 / 1_000_000, output: 50.00 / 1_000_000 },
  // https://developers.openai.com/api/docs/models/gpt-6.1-sol
  // Long-context (>272K prompt tokens) and regional surcharges cannot be inferred from usage buckets.
  'gpt-6.1-sol': { input: 2.00 / 1_000_000, cachedInput: 0.10 / 1_000_000, cacheWrite: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  // https://developers.openai.com/api/docs/models/gpt-6.1-terra
  // Uses 30m cache writes; per-request long context, 12h retention and regional surcharges are not inferable from usage buckets.
  'gpt-6.1-terra': { input: 1.00 / 1_000_000, cachedInput: 0.05 / 1_000_000, cacheWrite: 1.25 / 1_000_000, output: 5.00 / 1_000_000 },
  // https://developers.openai.com/api/docs/models/gpt-6-sol
  'gpt-6-sol': { input: 2.00 / 1_000_000, cachedInput: 0.20 / 1_000_000, cacheWrite: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  // https://developers.openai.com/api/docs/models/gpt-6-luna
  'gpt-6-luna': { input: 0.10 / 1_000_000, cachedInput: 0.01 / 1_000_000, cacheWrite: 0.125 / 1_000_000, output: 0.50 / 1_000_000 },
  // GPT-5.6 family (released 2026-07-10). Unlike GPT-5.5 / 5.4, the official
  // pricing table does not publish a separate long-context surcharge.
  // `gpt-5.6` aliases `gpt-5.6-sol`.
  'gpt-5.6': { input: 5.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, cacheWrite: 6.25 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.6-sol': { input: 5.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, cacheWrite: 6.25 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.6-terra': { input: 2.00 / 1_000_000, cachedInput: 0.20 / 1_000_000, cacheWrite: 2.50 / 1_000_000, output: 12.00 / 1_000_000 },
  'gpt-5.6-luna': { input: 0.20 / 1_000_000, cachedInput: 0.02 / 1_000_000, cacheWrite: 0.25 / 1_000_000, output: 1.20 / 1_000_000 },

  // GPT-5.5 (released 2026-04-23). Long-context surcharges (>272K prompt tokens) are not inferable from usage buckets.
  'gpt-5.5': { input: 5.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.5-2026-04-23': { input: 5.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.5-pro': { input: 30.00 / 1_000_000, output: 180.00 / 1_000_000 },
  'gpt-5.5-pro-2026-04-23': { input: 30.00 / 1_000_000, output: 180.00 / 1_000_000 },

  // GPT-5.4 long-context surcharges (>272K prompt tokens) are not inferable from usage buckets.
  'gpt-5.4': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 15.00 / 1_000_000 },
  'gpt-5.4-2026-03-05': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 15.00 / 1_000_000 },
  'gpt-5.4-mini': { input: 0.75 / 1_000_000, cachedInput: 0.075 / 1_000_000, output: 4.50 / 1_000_000 },
  'gpt-5.4-mini-2026-03-17': { input: 0.75 / 1_000_000, cachedInput: 0.075 / 1_000_000, output: 4.50 / 1_000_000 },
  'gpt-5.4-nano': { input: 0.20 / 1_000_000, cachedInput: 0.02 / 1_000_000, output: 1.25 / 1_000_000 },
  'gpt-5.4-nano-2026-03-17': { input: 0.20 / 1_000_000, cachedInput: 0.02 / 1_000_000, output: 1.25 / 1_000_000 },
  'gpt-5.4-pro': { input: 30.00 / 1_000_000, output: 180.00 / 1_000_000 },
  'gpt-5.4-pro-2026-03-05': { input: 30.00 / 1_000_000, output: 180.00 / 1_000_000 },

  // GPT-5.3 Models (Latest - 2026-02-05)
  'gpt-5.3-chat-latest': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-5.3-codex': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-5.3-codex-2026-02-05': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },

  // GPT-5.2 Models (Latest - 2025-12-11) - with cached input support
  'gpt-5.2': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-5.2-2025-12-11': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-5.2-chat-latest': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-5.2-codex': { input: 1.75 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-5.2-pro': { input: 21.00 / 1_000_000, output: 168.00 / 1_000_000 },
  'gpt-5.2-pro-2025-12-11': { input: 21.00 / 1_000_000, output: 168.00 / 1_000_000 },

  // GPT-5.1 Models (2025-11-13) - with cached input support
  'gpt-5.1': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5.1-2025-11-13': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5.1-codex': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5.1-codex-max': { input: 1.50 / 1_000_000, cachedInput: 0.150 / 1_000_000, output: 12.00 / 1_000_000 },
  'gpt-5.1-codex-mini': { input: 0.25 / 1_000_000, cachedInput: 0.025 / 1_000_000, output: 2.00 / 1_000_000 },

  // GPT-5 Models (Legacy - kept for compatibility) - with cached input support
  'gpt-5': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5-2025-08-07': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5-codex': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5-chat': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5-chat-latest': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5-mini': { input: 0.25 / 1_000_000, cachedInput: 0.025 / 1_000_000, output: 2.00 / 1_000_000 },
  'gpt-5-mini-2025-08-07': { input: 0.25 / 1_000_000, cachedInput: 0.025 / 1_000_000, output: 2.00 / 1_000_000 },
  'gpt-5-nano': { input: 0.05 / 1_000_000, cachedInput: 0.005 / 1_000_000, output: 0.40 / 1_000_000 },
  'gpt-5-nano-2025-08-07': { input: 0.05 / 1_000_000, cachedInput: 0.005 / 1_000_000, output: 0.40 / 1_000_000 },
  'gpt-5-pro': { input: 15.00 / 1_000_000, output: 120.00 / 1_000_000 },
  'gpt-5-pro-2025-10-06': { input: 15.00 / 1_000_000, output: 120.00 / 1_000_000 },

  // Latest GPT Models (Batch API pricing) - with cached input support
  'gpt-4.1': { input: 2.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 8.00 / 1_000_000 },
  'gpt-4.1-2025-04-14': { input: 2.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 8.00 / 1_000_000 },
  'gpt-4.1-mini': { input: 0.40 / 1_000_000, cachedInput: 0.10 / 1_000_000, output: 1.60 / 1_000_000 },
  'gpt-4.1-mini-2025-04-14': { input: 0.40 / 1_000_000, cachedInput: 0.10 / 1_000_000, output: 1.60 / 1_000_000 },
  'gpt-4.1-nano': { input: 0.10 / 1_000_000, cachedInput: 0.025 / 1_000_000, output: 0.40 / 1_000_000 },
  'gpt-4.1-nano-2025-04-14': { input: 0.10 / 1_000_000, cachedInput: 0.025 / 1_000_000, output: 0.40 / 1_000_000 },
  'gpt-4.5-preview': { input: 75.00 / 1_000_000, cachedInput: 37.50 / 1_000_000, output: 150.00 / 1_000_000 },
  'gpt-4.5-preview-2025-02-27': { input: 75.00 / 1_000_000, cachedInput: 37.50 / 1_000_000, output: 150.00 / 1_000_000 },

  // GPT-4o Models - with cached input support where available
  'gpt-4o': { input: 2.50 / 1_000_000, cachedInput: 1.25 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-2024-08-06': { input: 2.50 / 1_000_000, cachedInput: 1.25 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-audio-preview': { input: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-audio-preview-2024-12-17': { input: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-realtime-preview': { input: 5.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-4o-realtime-preview-2024-12-17': { input: 5.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-4o-realtime-preview-2025-06-03': { input: 5.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-4o-mini': { input: 0.15 / 1_000_000, cachedInput: 0.075 / 1_000_000, output: 0.60 / 1_000_000 },
  'gpt-4o-mini-2024-07-18': { input: 0.15 / 1_000_000, cachedInput: 0.075 / 1_000_000, output: 0.60 / 1_000_000 },
  'gpt-4o-mini-audio-preview': { input: 0.15 / 1_000_000, output: 0.60 / 1_000_000 },
  'gpt-4o-mini-audio-preview-2024-12-17': { input: 0.15 / 1_000_000, output: 0.60 / 1_000_000 },
  'gpt-4o-mini-realtime-preview': { input: 0.60 / 1_000_000, cachedInput: 0.30 / 1_000_000, output: 2.40 / 1_000_000 },
  'gpt-4o-mini-realtime-preview-2024-12-17': { input: 0.60 / 1_000_000, cachedInput: 0.30 / 1_000_000, output: 2.40 / 1_000_000 },
  'gpt-4o-search-preview': { input: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-search-preview-2025-03-11': { input: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-mini-search-preview': { input: 0.15 / 1_000_000, output: 0.60 / 1_000_000 },
  'gpt-4o-mini-search-preview-2025-03-11': { input: 0.15 / 1_000_000, output: 0.60 / 1_000_000 },

  // Reasoning Models - with cached input support where available
  'o1': { input: 15.00 / 1_000_000, cachedInput: 7.50 / 1_000_000, output: 60.00 / 1_000_000 },
  'o1-2024-12-17': { input: 15.00 / 1_000_000, cachedInput: 7.50 / 1_000_000, output: 60.00 / 1_000_000 },
  'o1-pro': { input: 150.00 / 1_000_000, output: 600.00 / 1_000_000 },
  'o1-pro-2025-03-19': { input: 150.00 / 1_000_000, output: 600.00 / 1_000_000 },
  'o3-pro': { input: 20.00 / 1_000_000, output: 80.00 / 1_000_000 },
  'o3-pro-2025-06-10': { input: 20.00 / 1_000_000, output: 80.00 / 1_000_000 },
  'o3': { input: 2.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 8.00 / 1_000_000 },
  'o3-2025-04-16': { input: 2.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 8.00 / 1_000_000 },
  'o3-deep-research': { input: 10.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 40.00 / 1_000_000 },
  'o3-deep-research-2025-06-26': { input: 10.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 40.00 / 1_000_000 },
  'o4-mini': { input: 1.10 / 1_000_000, cachedInput: 0.275 / 1_000_000, output: 4.40 / 1_000_000 },
  'o4-mini-2025-04-16': { input: 1.10 / 1_000_000, cachedInput: 0.275 / 1_000_000, output: 4.40 / 1_000_000 },
  'o4-mini-deep-research': { input: 2.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 8.00 / 1_000_000 },
  'o4-mini-deep-research-2025-06-26': { input: 2.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, output: 8.00 / 1_000_000 },
  'o3-mini': { input: 1.10 / 1_000_000, cachedInput: 0.55 / 1_000_000, output: 4.40 / 1_000_000 },
  'o3-mini-2025-01-31': { input: 1.10 / 1_000_000, cachedInput: 0.55 / 1_000_000, output: 4.40 / 1_000_000 },
  'o1-mini': { input: 1.10 / 1_000_000, cachedInput: 0.55 / 1_000_000, output: 4.40 / 1_000_000 },
  'o1-mini-2024-09-12': { input: 1.10 / 1_000_000, cachedInput: 0.55 / 1_000_000, output: 4.40 / 1_000_000 },

  // Other Models - with cached input support where available
  'codex-mini-latest': { input: 1.50 / 1_000_000, cachedInput: 0.375 / 1_000_000, output: 6.00 / 1_000_000 },
  'computer-use-preview': { input: 3.00 / 1_000_000, output: 12.00 / 1_000_000 },
  'computer-use-preview-2025-03-11': { input: 3.00 / 1_000_000, output: 12.00 / 1_000_000 },
  'gpt-5-search-api': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-5-search-api-2025-10-14': { input: 1.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-image-1': { input: 10.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 40.00 / 1_000_000 },
  'gpt-image-1-2025-04-23': { input: 10.00 / 1_000_000, cachedInput: 2.50 / 1_000_000, output: 40.00 / 1_000_000 },
  'gpt-image-1.5': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 32.00 / 1_000_000 },
  'gpt-image-1.5-2025-12-16': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 32.00 / 1_000_000 },
  // gpt-image-2 (released 2026-04-21, API GA early May 2026). Token-billed like gpt-image-1 系.
  // Image input $8 / cached image input $2 / image output $30 per 1M (output dropped from $32 vs 1.5).
  // Text input is $5/1M, but images usage API merges input_tokens, so we follow the gpt-image-1.5
  // convention of using the image-input rate for `input`.
  'gpt-image-2': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 30.00 / 1_000_000 },
  // GPT-Image 2.5 (released 2026-09-08). `sunburst` targets high-precision
  // editing, `flare` fast everyday generation; both are priced identically to
  // gpt-image-2. Text input is $5/1M and cached text input $1.25/1M, but the
  // images usage API merges text and image counts into input_tokens, so we keep
  // the gpt-image-1.5 convention of billing `input` at the image-input rate.
  'gpt-image-2.5-sunburst': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-image-2.5-sunburst-2026-09-08': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-image-2.5-flare': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-image-2.5-flare-2026-09-08': { input: 8.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-image-1-mini': { input: 2.50 / 1_000_000, cachedInput: 0.25 / 1_000_000, output: 8.00 / 1_000_000 },
  'chatgpt-4o-latest': { input: 5.00 / 1_000_000, output: 15.00 / 1_000_000 },

  // GPT-4 Legacy Models
  'gpt-4-turbo': { input: 10.00 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-4-turbo-2024-04-09': { input: 10.00 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-4': { input: 30.00 / 1_000_000, output: 60.00 / 1_000_000 },
  'gpt-4-0613': { input: 30.00 / 1_000_000, output: 60.00 / 1_000_000 },
  'gpt-4-32k': { input: 60.00 / 1_000_000, output: 120.00 / 1_000_000 },

  // GPT-3.5 Models
  'gpt-3.5-turbo': { input: 0.50 / 1_000_000, output: 1.50 / 1_000_000 },
  'gpt-3.5-turbo-0125': { input: 0.50 / 1_000_000, output: 1.50 / 1_000_000 },
  'gpt-3.5-turbo-instruct': { input: 1.50 / 1_000_000, output: 2.00 / 1_000_000 },
  'gpt-3.5-turbo-16k-0613': { input: 3.00 / 1_000_000, output: 4.00 / 1_000_000 },

  // Base Models
  'davinci-002': { input: 2.00 / 1_000_000, output: 2.00 / 1_000_000 },
  'babbage-002': { input: 0.40 / 1_000_000, output: 0.40 / 1_000_000 },

  // Embedding Models
  'text-embedding-3-large': { input: 0.13 / 1_000_000, output: 0 },
  'text-embedding-3-small': { input: 0.02 / 1_000_000, output: 0 },
  'text-embedding-ada-002': { input: 0.10 / 1_000_000, output: 0 },
  'text-embedding-ada-002-v2': { input: 0.10 / 1_000_000, output: 0 },

  // Image Generation Models (per image)
  'dall-e-3': { input: 0.04, output: 0 }, // Standard 1024x1024
  'dall-e-2': { input: 0.02, output: 0 }, // Standard 1024x1024

  // Audio Models
  'whisper-1': { input: 0.006 / 60, output: 0 }, // per second
  'gpt-4o-mini-tts': { input: 0.60 / 1_000_000, output: 12.00 / 1_000_000 },
  'gpt-4o-mini-tts-2025-12-15': { input: 0.60 / 1_000_000, output: 12.00 / 1_000_000 },
  'gpt-4o-transcribe': { input: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  'gpt-4o-mini-transcribe': { input: 1.25 / 1_000_000, output: 5.00 / 1_000_000 },
  'tts-1': { input: 15.00 / 1_000_000, output: 0 }, // per character
  'tts-1-hd': { input: 30.00 / 1_000_000, output: 0 }, // per character
};

// Audio transcription per-second pricing.
// The audio_transcriptions usage API reports seconds (not tokens), so token-based
// entries in modelPricing cannot be used directly for these models.
export const audioTranscriptionPerSecondPricing: Record<string, number> = {
  'whisper-1': 0.006 / 60,
  'gpt-4o-transcribe': 0.006 / 60,
  'gpt-4o-mini-transcribe': 0.003 / 60,
};

// Priority Tier Pricing (for service_tier = "priority" / legacy "priority-tier")
export const priorityTierPricing: Record<string, ModelPricingEntry> = {
  // GPT-6 Astra Fast mode (also accepts service_tier=priority): 2x standard.
  'gpt-6-astra': { input: 20.00 / 1_000_000, cachedInput: 2.00 / 1_000_000, cacheWrite: 25.00 / 1_000_000, output: 100.00 / 1_000_000 },
  // GPT-6.1 Sol Fast mode (also accepts service_tier=priority): 2x standard.
  'gpt-6.1-sol': { input: 4.00 / 1_000_000, cachedInput: 0.20 / 1_000_000, cacheWrite: 5.00 / 1_000_000, output: 20.00 / 1_000_000 },
  // GPT-6.1 Terra Fast mode (also accepts service_tier=priority): 2x standard.
  'gpt-6.1-terra': { input: 2.00 / 1_000_000, cachedInput: 0.10 / 1_000_000, cacheWrite: 2.50 / 1_000_000, output: 10.00 / 1_000_000 },
  // GPT-6 Sol and Luna Fast mode (also accepts service_tier=priority): 2x standard.
  'gpt-6-sol': { input: 4.00 / 1_000_000, cachedInput: 0.40 / 1_000_000, cacheWrite: 5.00 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-6-luna': { input: 0.20 / 1_000_000, cachedInput: 0.02 / 1_000_000, cacheWrite: 0.25 / 1_000_000, output: 1.00 / 1_000_000 },
  // GPT-5.6 priority tier
  'gpt-5.6': { input: 10.00 / 1_000_000, cachedInput: 1.00 / 1_000_000, cacheWrite: 12.50 / 1_000_000, output: 60.00 / 1_000_000 },
  'gpt-5.6-sol': { input: 10.00 / 1_000_000, cachedInput: 1.00 / 1_000_000, cacheWrite: 12.50 / 1_000_000, output: 60.00 / 1_000_000 },
  'gpt-5.6-terra': { input: 5.00 / 1_000_000, cachedInput: 0.50 / 1_000_000, cacheWrite: 6.25 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.6-luna': { input: 2.00 / 1_000_000, cachedInput: 0.20 / 1_000_000, cacheWrite: 2.50 / 1_000_000, output: 12.00 / 1_000_000 },

  // GPT-5.5 priority tier = 2.5x standard
  'gpt-5.5': { input: 12.50 / 1_000_000, cachedInput: 1.25 / 1_000_000, output: 75.00 / 1_000_000 },
  'gpt-5.5-2026-04-23': { input: 12.50 / 1_000_000, cachedInput: 1.25 / 1_000_000, output: 75.00 / 1_000_000 },

  'gpt-5.4': { input: 5.00 / 1_000_000, cachedInput: 0.500 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.4-2026-03-05': { input: 5.00 / 1_000_000, cachedInput: 0.500 / 1_000_000, output: 30.00 / 1_000_000 },
  'gpt-5.4-mini': { input: 1.50 / 1_000_000, cachedInput: 0.150 / 1_000_000, output: 9.00 / 1_000_000 },
  'gpt-5.4-mini-2026-03-17': { input: 1.50 / 1_000_000, cachedInput: 0.150 / 1_000_000, output: 9.00 / 1_000_000 },
  'gpt-5.4-nano': { input: 0.40 / 1_000_000, cachedInput: 0.04 / 1_000_000, output: 2.50 / 1_000_000 },
  'gpt-5.4-nano-2026-03-17': { input: 0.40 / 1_000_000, cachedInput: 0.04 / 1_000_000, output: 2.50 / 1_000_000 },

  // GPT-5.3 Models (Latest - 2026-02-05)
  'gpt-5.3-chat-latest': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },
  'gpt-5.3-codex': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },
  'gpt-5.3-codex-2026-02-05': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },

  // GPT-5.2 Models (Latest - 2025-12-11)
  'gpt-5.2': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },
  'gpt-5.2-2025-12-11': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },
  'gpt-5.2-chat-latest': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },
  'gpt-5.2-codex': { input: 3.50 / 1_000_000, cachedInput: 0.350 / 1_000_000, output: 28.00 / 1_000_000 },

  // GPT-5.1 Models (2025-11-13)
  'gpt-5.1': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5.1-2025-11-13': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5.1-codex': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },

  // GPT-5 Models (Legacy - kept for compatibility)
  'gpt-5': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5-2025-08-07': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5-codex': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5-chat': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5-chat-latest': { input: 2.50 / 1_000_000, cachedInput: 0.250 / 1_000_000, output: 20.00 / 1_000_000 },
  'gpt-5-mini': { input: 0.45 / 1_000_000, cachedInput: 0.045 / 1_000_000, output: 3.60 / 1_000_000 },
  'gpt-5-mini-2025-08-07': { input: 0.45 / 1_000_000, cachedInput: 0.045 / 1_000_000, output: 3.60 / 1_000_000 },

  // GPT-4.1 Models
  'gpt-4.1': { input: 3.50 / 1_000_000, cachedInput: 0.875 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-4.1-2025-04-14': { input: 3.50 / 1_000_000, cachedInput: 0.875 / 1_000_000, output: 14.00 / 1_000_000 },
  'gpt-4.1-mini': { input: 0.70 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 2.80 / 1_000_000 },
  'gpt-4.1-mini-2025-04-14': { input: 0.70 / 1_000_000, cachedInput: 0.175 / 1_000_000, output: 2.80 / 1_000_000 },
  'gpt-4.1-nano': { input: 0.20 / 1_000_000, cachedInput: 0.050 / 1_000_000, output: 0.80 / 1_000_000 },
  'gpt-4.1-nano-2025-04-14': { input: 0.20 / 1_000_000, cachedInput: 0.050 / 1_000_000, output: 0.80 / 1_000_000 },

  // GPT-4o Models
  'gpt-4o': { input: 4.25 / 1_000_000, cachedInput: 2.125 / 1_000_000, output: 17.00 / 1_000_000 },
  'gpt-4o-2024-08-06': { input: 4.25 / 1_000_000, cachedInput: 2.125 / 1_000_000, output: 17.00 / 1_000_000 },
  'gpt-4o-2024-11-20': { input: 4.25 / 1_000_000, cachedInput: 2.125 / 1_000_000, output: 17.00 / 1_000_000 },
  'gpt-4o-2024-05-13': { input: 8.75 / 1_000_000, output: 26.25 / 1_000_000 }, // No cached pricing
  'gpt-4o-mini': { input: 0.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 1.00 / 1_000_000 },
  'gpt-4o-mini-2024-07-18': { input: 0.25 / 1_000_000, cachedInput: 0.125 / 1_000_000, output: 1.00 / 1_000_000 },

  // Reasoning Models
  'o3': { input: 3.50 / 1_000_000, cachedInput: 0.875 / 1_000_000, output: 14.00 / 1_000_000 },
  'o3-2025-04-16': { input: 3.50 / 1_000_000, cachedInput: 0.875 / 1_000_000, output: 14.00 / 1_000_000 },
  'o4-mini': { input: 2.00 / 1_000_000, cachedInput: 0.500 / 1_000_000, output: 8.00 / 1_000_000 },
  'o4-mini-2025-04-16': { input: 2.00 / 1_000_000, cachedInput: 0.500 / 1_000_000, output: 8.00 / 1_000_000 },
};
